import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import eventService from '../../services/eventService';
import { toast } from 'react-toastify';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";

// Add style to ensure Flatpickr calendar has highest z-index
const style = document.createElement('style');
style.textContent = `
  .flatpickr-calendar {
    z-index: 99999 !important;
  }
`;
if (!document.head.querySelector('style[data-flatpickr-fix]')) {
  style.setAttribute('data-flatpickr-fix', 'true');
  document.head.appendChild(style);
}

const EventList = () => {
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const PER_PAGE = 6;
    const [totalEvents, setTotalEvents] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isDateOpen, setIsDateOpen] = useState(false);
    const [topEvents, setTopEvents] = useState([]);
    const [topIndex, setTopIndex] = useState(0);
    const [featuredEvents, setFeaturedEvents] = useState([]);
    const topCategories = categories.filter(
        c => Number(c.top_category) === 1
    );
    const [pauseTopCarousel, setPauseTopCarousel] = useState(false);

    useEffect(() => {
        if (!topEvents.length || pauseTopCarousel || isDateOpen) return;

        const interval = setInterval(() => {
            setTopIndex(prev =>
                prev < topEvents.length - 1 ? prev + 1 : 0
            );
        }, 5000);

        return () => clearInterval(interval);
    }, [topEvents, pauseTopCarousel, isDateOpen]);

    useEffect(() => {
        const delay = setTimeout(() => {
            setCurrentPage(1);
            fetchEvents(1);
        }, 500);

        return () => clearTimeout(delay);
    }, [searchTerm, selectedDate]);

// Pagination & category
    useEffect(() => {
        fetchEvents(currentPage);
    }, [currentPage, selectedCategory]);

// Categories once
    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        setCurrentIndex(0);
    }, [events, selectedCategory, searchTerm, selectedDate]);

    const preloadImages = (items = []) => {
        items.forEach(event => {
            if (event?.images?.[0]?.image_path) {
                const img = new Image();
                img.src = event.images[0].image_path;
            }
        });
    };

    useEffect(() => {
        if (events.length) {
            preloadImages(events);
        }
    }, [events]);

    useEffect(() => {
        const fetchTopEvents = async () => {
            try {
                const res = await eventService.getTopEvents();
                setTopEvents(res.data.data || []);
            } catch (e) {
                console.error(e);
            }
        };

        fetchTopEvents();
    }, []);

    useEffect(() => {
        const fetchFeaturedEvents = async () => {
            try {
                const res = await eventService.getFeaturedEvents();
                setFeaturedEvents(res.data.data || []);
            } catch (e) {
                console.error('Error fetching featured events:', e);
            }
        };
        fetchFeaturedEvents();
    }, []);



    const nextSlide = () => {
        setCurrentIndex((prev) => (prev + 1) % events.length);
    };

    const prevSlide = () => {
        setCurrentIndex((prev) => (prev - 1 + events.length) % events.length);
    };

    const currentEvent =
        Array.isArray(events) && events.length > 0
            ? events[currentIndex % events.length]
            : null;


// Auto play
    useEffect(() => {
        if (!events.length || isDateOpen) return;

        const interval = setInterval(nextSlide, 8000);
        return () => clearInterval(interval);
    }, [events, currentIndex, isDateOpen]);

    const fetchEvents = async (page = 1) => {
        try {
            setLoading(true);

            const params = {
                page,
                per_page: PER_PAGE,
            };

            if (selectedCategory !== 'all') {
                params.category_id = selectedCategory;
            }

            if (selectedDate) {
                params.search = selectedDate;
            } else if (searchTerm.trim()) {
                params.search = searchTerm;
            }

            const response = await eventService.getEvents(params);

            // ✅ Laravel paginator structure
            const paginated = response.data.data;

            setEvents(paginated.data);
            setCurrentPage(paginated.current_page);
            setLastPage(paginated.last_page);
            setTotalEvents(paginated.total);

        } catch (error) {
            console.error('Error fetching events:', error);
            toast.error('Failed to load events');
            setEvents([]);
        } finally {
            setLoading(false);
        }
    };

  const fetchCategories = async () => {
    try {
      const response = await eventService.getCategories();
      console.log('Categories API Response:', response.data);
      
      // Categories response structure: { success: true, data: [...] }
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setCategories(response.data.data);
      } else {
        console.error('Unexpected categories data structure:', response.data);
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

    const getPaginationPages = () => {
        const pages = [];
        const delta = 1; // how many pages around current

        pages.push(1);

        if (currentPage - delta > 2) {
            pages.push('...');
        }

        for (
            let i = Math.max(2, currentPage - delta);
            i <= Math.min(lastPage - 1, currentPage + delta);
            i++
        ) {
            pages.push(i);
        }

        if (currentPage + delta < lastPage - 1) {
            pages.push('...');
        }

        if (lastPage > 1) {
            pages.push(lastPage);
        }

        return [...new Set(pages)];
    };

    const getCategoryImage = (category) => {
        const eventWithImage = category.events?.find(
            e => e.images?.length > 0
        );

        return eventWithImage?.images?.[0]?.image_path
            ? `${process.env.REACT_APP_URL}/storage/${eventWithImage.images[0].image_path}`
            : null;
    };


    if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 mx-auto text-yellow-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-yellow-400">Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
        {/* Header Carousel */}
        {currentEvent && (
            <section className="relative h-[92vh] overflow-hidden text-white">

                {/* Slides */}
                <div className="absolute inset-0 pointer-events-none">
                    {events.map((event, i) => {
                        const image =
                            event?.images && event.images.length > 0
                                ? `${process.env.REACT_APP_URL}/storage/${event.images[0].image_path}`
                                : null;

                        const video = event?.video_link
                            ? `${process.env.REACT_APP_URL}/storage/${event.video_link}`
                            : null;

                        return (
                            <div
                                key={event.id ?? i}
                                className={`absolute inset-0 transition-opacity duration-1000 ${
                                    i === currentIndex ? 'opacity-100' : 'opacity-0'
                                }`}
                            >
                                {video ? (
                                    <video
                                        src={video}
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        className="w-full h-full object-cover"
                                    />
                                ) : image ? (
                                    <img
                                        src={image}
                                        alt={event.title}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600" />
                                )}
                            </div>
                        );
                    })}

                </div>

                {/* Dark Overlay */}
                <div className="absolute inset-0 bg-black/50 pointer-events-none" />

                {/* TOP LEFT – Category */}
                {currentEvent.category && (
                    <div className="absolute top-6 left-6 z-20 pointer-events-none">
        <span className="px-4 py-1 rounded-full bg-white/20 backdrop-blur-md text-sm font-semibold">
          {currentEvent.category.icon} {currentEvent.category.category_name}
        </span>
                    </div>
                )}

                {/* CENTER – Title & CTA */}
                <div className="relative z-20 h-full flex items-center justify-center text-center pointer-events-auto">
                    <div className="px-6 max-w-3xl">
                        <h1 className="text-4xl sm:text-5xl font-extrabold mb-6 leading-tight">
                            {currentEvent.title}
                        </h1>

                        <Link
                            to={`/events/${currentEvent.id}`}
                            className="inline-block bg-white/20 text-yellow-400 border-2 border-yellow-400 px-10 py-4 rounded-lg font-semibold
                     hover:bg-yellow-400 hover:text-white no-underline transition shadow-lg no-underline hover:no-underline"
                        >
                            View Event & Tickets
                        </Link>
                    </div>
                </div>

                {/* BOTTOM CENTER – Date & Location */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 text-gray-200 pointer-events-none w-full px-4">
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 justify-center items-center text-sm sm:text-base text-center">

                        {/* Date */}
                        <div className="flex items-center justify-center">
                            <svg
                                className="w-5 h-5 mr-2 text-yellow-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                            </svg>
                            {formatDate(currentEvent.event_start)}
                        </div>

                        {/* Location */}
                        {currentEvent.location && (
                            <div className="flex items-center justify-center">
                                <svg
                                    className="w-5 h-5 mr-2 text-yellow-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M12 11a3 3 0 100-6 3 3 0 000 6z"
                                    />
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M19.5 10.5C19.5 16 12 21 12 21S4.5 16 4.5 10.5a7.5 7.5 0 1115 0z"
                                    />
                                </svg>
                                {currentEvent.location}
                            </div>
                        )}
                    </div>
                </div>

                {/* Navigation */}
                <div className="absolute inset-0 flex items-center justify-between px-6 z-20 pointer-events-none">
                    <button
                        onClick={prevSlide}
                        className="pointer-events-auto w-12 h-12 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-2xl"
                    >
                        ‹
                    </button>
                    <button
                        onClick={nextSlide}
                        className="pointer-events-auto w-12 h-12 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-2xl"
                    >
                        ›
                    </button>
                </div>

            </section>
        )}

        <div className="relative  max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 ">

            {/* SEARCH BAR */}
            <div className="mb-8 flex flex-col sm:flex-row gap-4 pointer-events-auto">

                {/* Keyword search */}
                <div className="relative flex-1">
                    <input
                        type="text"
                        placeholder="Search event or location"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 !border !border-yellow-400 rounded-full focus:ring-2 focus:ring-yellow-400 outline-none"
                    />
                    <svg
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-yellow-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                              d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </div>

                {/* Date search */}
                <div className="relative z-[9999]">
                    <Flatpickr
                        value={selectedDate}
                        onOpen={() => {
                            console.log('Flatpickr opened');
                            setIsDateOpen(true);
                        }}
                        onClose={() => {
                            console.log('Flatpickr closed');
                            setIsDateOpen(false);
                        }}
                        onChange={(dates, dateStr) => {
                            setSelectedDate(dateStr);
                        }}
                        options={{
                            dateFormat: "Y-m-d",
                            altFormat: "F j, Y",
                            clickOpens: true,
                            allowInput: false,
                        }}
                        placeholder="Select a date"
                        className="pl-12 pr-4 py-3 !border !border-yellow-400 rounded-full focus:ring-2 focus:ring-yellow-400 outline-none w-full cursor-pointer"
                    />

                    <svg
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-yellow-400 pointer-events-none"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                    </svg>
                    {/* Clear button */}
                    {selectedDate && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedDate("");
                            }}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 z-10"
                        >
                            ✕
                        </button>
                    )}

                </div>
            </div>
        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-3 pointer-events-auto">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-6 py-2  rounded-full font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-yellow-400 border border-gray-900 text-white shadow-lg'
                  : 'bg-white text-gray-700 border border-gray-100 hover:bg-gray-100'
              }`}
            >
              All Events
            </button>
            {Array.isArray(categories) && categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6  py-2 rounded-full font-medium transition-all ${
                  selectedCategory === category.id
                    ? 'bg-yellow-400 border border-gray-900 text-white shadow-lg' 
                    : 'bg-white border border-gray-900 text-gray-700 hover:bg-yellow-400'
                }`}
              >
                 {category.category_name}
              </button>
            ))}
          </div>
        </div>

            {topCategories.length > 0 && (
                <section className="w-full px-4 sm:px-6 lg:px-8 mt-16 mb-12">
                    <h2 className="text-xl font-bold text-gray-900 mb-8">
                        Top Categories
                    </h2>

                    <div className="flex flex-wrap gap-8 ">
                        {topCategories.map(category => {
                            const image = getCategoryImage(category);

                            return (
                                <Link
                                    to="/events"
                                    onClick={() => {
                                        setSelectedCategory(category.id);
                                        setCurrentPage(1);
                                    }}
                                    className="flex flex-col items-center group no-underline"
                                >
                                    {/* Circle image */}
                                    <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-yellow-400 shadow-lg mb-3
                            group-hover:scale-105 transition">
                                        {image ? (
                                            <img
                                                src={image}
                                                alt={category.category_name}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-600">
                                                {category.icon || '🎫'}
                                            </div>
                                        )}
                                    </div>

                                    {/* Name */}
                                    <span className="text-sm font-semibold text-gray-900 group-hover:text-yellow-400 transition text-center">
                                  {category.category_name}
                                </span>
                                </Link>
                            );
                        })}
                    </div>
                </section>
            )}

        {/* Events Count */}
        <div className="mb-6">
          <p className="text-gray-900">
            <span className="font-semibold text-gray-900">{totalEvents}</span> events found
          </p>
        </div>

        {/* Events Grid */}
        {!Array.isArray(events) || events.length === 0 ? (
          <div className="text-center py-16">
            <svg className="mx-auto h-24 w-24 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <h3 className="mt-4 text-xl font-medium text-gray-900">No events found</h3>
            <p className="mt-2 text-gray-500">Try selecting a different category</p>
          </div>
            ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {events.map((event) => (
              <Link
                key={event.id}
                to={`/events/${event.id}`}
                className="group card overflow-hidden bg-white shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 no-underline hover:no-underline"
              >
                {/* Event Image */}
                <div className="relative h-64 overflow-hidden">
                    {event.images && event.images.length > 0 ? (
                        <img
                            src={`${process.env.REACT_APP_URL}/storage/${event.images[0].image_path}`}
                            alt={event.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src =
                                    'https://via.placeholder.com/400x300/6366f1/ffffff?text=' +
                                    encodeURIComponent(event.title);
                            }}
                        />
                    ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center">
                      <div className="text-center text-white p-6">
                        <svg className="w-20 h-20 mx-auto mb-4 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-sm !text-black font-semibold">{event.category?.name || 'Event'}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  
                  {/* Featured Badge */}
                  {event.featured_event === 1 && (
                    <div className="absolute top-4 right-4 z-10">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-yellow-400 text-yellow-900 shadow-lg">
                        ⭐ Featured
                      </span>
                    </div>
                  )}
                  
                  {/* Category Badge */}
                  {event.category && (
                    <div className="absolute top-4 left-4 z-10">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-white/90 text-black shadow-lg backdrop-blur-sm">
                        {event.category.icon} {event.category.category_name}
                      </span>
                    </div>
                  )}
                </div>

                {/* Event Details */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-purple-600 transition-colors">
                    {event.title}
                  </h3>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3 leading-relaxed">
                    {event.description}
                  </p>

                  <div className="space-y-3 mb-4">
                    {/* Date */}
                    <div className="flex items-center text-sm text-gray-700">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-yellow-400 mr-3">
                        <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="mt-3">
                        <p className="font-semibold text-gray-900">{formatDate(event.event_start)}</p>
                      </div>
                    </div>

                    {/* Location */}
                    <div className="flex items-center text-sm text-gray-700">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-yellow-400 mr-3">
                        <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0 mt-3">
                        <p className="font-semibold text-gray-900 truncate">{event.location}</p>
                      </div>
                    </div>
                  </div>

                  {/* View Details Button */}
                  <button className="  rounded-md h-12 text-black bg-white border-2 border-yellow-400  w-full group-hover:!bg-yellow-400 group-hover:!text-black transition-all duration-300">
                    <span className="flex items-center justify-center">
                      View Details & Get Tickets
                      <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </span>
                  </button>
                </div>
              </Link>
            ))}
          </div>
        )}
            {lastPage > 1 && (
                <div className="flex justify-center mt-12 mb-10 gap-2 items-center">
                    <button
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => p - 1)}
                        className="px-4 py-2 border rounded disabled:opacity-40"
                    >
                        Previous
                    </button>

                    {getPaginationPages().map((page, index) =>
                        page === '...' ? (
                            <span key={index} className="px-2 text-gray-500">
                              ...
                            </span>
                        ) : (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`px-4 py-2 border rounded ${
                                    page === currentPage
                                        ? 'bg-yellow-400 text-black'
                                        : 'bg-white hover:bg-gray-100'
                                }`}
                            >
                                {page}
                            </button>
                        )
                    )}

                    <button
                        disabled={currentPage === lastPage}
                        onClick={() => setCurrentPage(p => p + 1)}
                        className="px-4 py-2 border rounded disabled:opacity-40"
                    >
                        Next
                    </button>
                </div>
            )}

            {topEvents.length > 0 && (
                <section className="w-full px-4 sm:px-6 lg:px-8 mt-10 mb-12">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4 max-w-7xl mx-auto">
                        <h2 className="text-xl font-bold text-gray-900">
                            Top Events
                        </h2>

                        {/* Arrows */}
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    setPauseTopCarousel(true);
                                    setTopIndex(i => Math.max(i - 1, 0));
                                }}
                                disabled={topIndex === 0}
                                className="w-9 h-9 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-40 flex items-center justify-center"
                            >
                                ‹
                            </button>

                            <button
                                onClick={() => {
                                    setPauseTopCarousel(true);
                                    setTopIndex(i => Math.min(i + 1, topEvents.length - 1));
                                }}
                                disabled={topIndex >= topEvents.length - 1}
                                className="w-9 h-9 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-40 flex items-center justify-center"
                            >
                                ›
                            </button>
                        </div>
                    </div>

                    {/* Carousel */}
                    {/* IMPORTANT: overflow-x-hidden ONLY */}
                    <div className="overflow-x-hidden rounded-xl shadow-md" onMouseEnter={() => setPauseTopCarousel(true)}
                         onMouseLeave={() => setPauseTopCarousel(false)}>
                        <div
                            className="flex transition-transform duration-500 max-w-7xl mx-auto"
                            style={{
                                transform: `translateX(-${topIndex * 100}%)`,
                            }}
                        >
                            {topEvents.map(event => (
                                <Link
                                    key={event.id}
                                    to={`/events/${event.id}`}
                                    className="min-w-full bg-white rounded-xl border border-gray-200 shadow-lg hover:shadow-2xl transition flex flex-col md:flex-row overflow-hidden lg:h-[280px] no-underline hover:no-underline"
                                >
                                    {/* Image */}
                                    <img
                                        src={
                                            event.images?.[0]?.image_path
                                                ? `${process.env.REACT_APP_URL}/storage/${event.images[0].image_path}`
                                                : 'https://via.placeholder.com/600x400?text=Event'
                                        }
                                        alt={event.title}
                                        className="w-full h-64 sm:h-72 md:w-1/2 lg:h-full object-cover"
                                        onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = 'https://via.placeholder.com/600x400?text=Event';
                                        }}
                                    />

                                    {/* Content */}
                                    <div className="p-4 sm:p-6 flex flex-col justify-between md:w-1/2">
                                        <div>
                                            <h3 className="font-extrabold text-gray-900 text-lg line-clamp-2">
                                                {event.title}
                                            </h3>

                                            <div className="space-y-1 mt-3">
                                                {/* Location */}
                                                <div className="flex items-center text-sm text-gray-900">
                                                    <svg
                                                        className="w-4 h-4 mr-1 text-yellow-400"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="2"
                                                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                                        />
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="2"
                                                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                                        />
                                                    </svg>
                                                    <span>{event.location}</span>
                                                </div>

                                                {/* Date */}
                                                <div className="flex items-center text-sm text-gray-900">
                                                    <svg
                                                        className="w-4 h-4 mr-1 text-yellow-400"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="2"
                                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                        />
                                                    </svg>
                                                    <span>{formatDate(event.event_start)}</span>
                                                </div>

                                                {/* Cheapest price */}
                                                {event.ticket_categories?.length > 0 && (
                                                    <div className="flex items-center text-sm text-gray-900 mt-1">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="w-5 h-5 mr-1"
                                                            viewBox="0 0 24 24"
                                                            fill="#FACC15"
                                                        >
                                                            <path d="M9 10a1 1 0 0 0-1 1v2a1 1 0 0 0 2 0v-2a1 1 0 0 0-1-1Zm12 1a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1a1 1 0 0 1 0 2a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1a1 1 0 0 1 0-2Zm-1-1.82a3 3 0 0 0 0 5.64V17H10a1 1 0 0 0-2 0H4v-2.18a3 3 0 0 0 0-5.64V7h4a1 1 0 0 0 2 0h10Z" />
                                                        </svg>

                                                        <span>
                        From {Math.min(...event.ticket_categories.map(tc => tc.price))} {event.currency}
                      </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <span className="text-yellow-400 font-semibold mt-4">
                View →
              </span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {featuredEvents.length > 0 && (
                <section className="w-full px-4 sm:px-6 lg:px-8 mt-10 mb-12">
                    <h2 className="text-xl font-bold text-gray-900 mb-6">
                        Featured Events
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {featuredEvents.map((event) => (
                            <Link
                                key={event.id}
                                to={`/events/${event.id}`}
                                className="group card overflow-hidden bg-white shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 no-underline hover:no-underline"
                            >
                                {/* Event Image */}
                                <div className="relative h-64 overflow-hidden">
                                    {event.images && event.images.length > 0 ? (
                                        <img
                                            src={`${process.env.REACT_APP_URL}/storage/${event.images[0].image_path}`}
                                            alt={event.title}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                            onError={(e) => {
                                                e.currentTarget.onerror = null;
                                                e.currentTarget.src =
                                                    'https://via.placeholder.com/400x300/6366f1/ffffff?text=' +
                                                    encodeURIComponent(event.title);
                                            }}
                                        />
                                    ) : (
                                        <div className="absolute inset-0 bg-gray-300 flex items-center justify-center">
                                            <span className="text-gray-700">No Image</span>
                                        </div>
                                    )}


                                    {/* Badges */}
                                    <div className="absolute top-4 left-4 flex flex-col gap-2 z-20">
                                        {event.featured_event === 1 && (
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-yellow-400 text-black shadow-lg">
                                               Featured
                                            </span>
                                        )}
                                        {event.best_seller && (
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow-400 text-black shadow-lg">
                                               Best Seller
                                            </span>
                                        )}
                                        {event.new_event && (
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow-400 text-black shadow-lg">
                                               New Event
                                            </span>
                                        )}
                                    </div>


                                </div>

                                {/* Event Details */}
                                <div className="p-6">
                                    <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-purple-600 transition-colors">
                                        {event.title}
                                    </h3>

                                    <div className="space-y-2 text-sm text-gray-700">
                                        <div className="flex items-center">
                                            <svg className="w-5 h-5 mr-2 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            {formatDate(event.event_start)}
                                        </div>

                                        <div className="flex items-center">
                                            <svg className="w-5 h-5 mr-2 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                            {event.location}
                                        </div>
                                        {event.ticket_categories && event.ticket_categories.length > 0 && (
                                            <div className="flex items-center text-sm text-gray-700 mt-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="#FACC15" d="M9 10a1 1 0 0 0-1 1v2a1 1 0 0 0 2 0v-2a1 1 0 0 0-1-1Zm12 1a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1a1 1 0 0 1 0 2a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1a1 1 0 0 1 0-2Zm-1-1.82a3 3 0 0 0 0 5.64V17H10a1 1 0 0 0-2 0H4v-2.18a3 3 0 0 0 0-5.64V7h4a1 1 0 0 0 2 0h10Z"/></svg>

                                                <span className="pl-2">
                                                     From {Math.min(...event.ticket_categories.map(tc => tc.price))}{" "}{event.currency}
                                                    </span>
                                            </div>
                                        )}
                                    </div>


                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            <section className="w-full px-4 sm:px-6 lg:px-8 mt-16 bg-gray-100 border py-4 rounded-xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-10">
                    Why buy tickets with <span className="text-yellow-400">Ticketaty ?</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">

                    {/* Lightning Fast */}
                    <div className="flex flex-col items-center">
                        <div className="w-12 h-12 flex items-center justify-center mb-3">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#FACC15" strokeWidth="2">
                                <path d="M13 2L3 14h7l-1 8l10-12h-7l1-8z"/>
                            </svg>
                        </div>
                        <h3 className="font-semibold text-lg text-gray-900">Lightning Fast</h3>
                        <p className="text-sm text-gray-600 mt-2">
                            Book your tickets in seconds with our streamlined checkout process. No hassle, no delays.
                        </p>
                    </div>

                    {/* Digital Tickets */}
                    <div className="flex flex-col items-center">
                        <div className="w-12 h-12 flex items-center justify-center mb-3">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#FACC15" strokeWidth="2">
                                <path d="M6 2h12v20H6z"/>
                                <path d="M9 6h6M9 10h6M9 14h4"/>
                            </svg>
                        </div>
                        <h3 className="font-semibold text-lg text-gray-900">Digital Tickets</h3>
                        <p className="text-sm text-gray-600 mt-2">
                            Get instant QR code tickets on your phone. Eco-friendly and always accessible.
                        </p>
                    </div>

                    {/* Secure Payment */}
                    <div className="flex flex-col items-center">
                        <div className="w-12 h-12 flex items-center justify-center mb-3">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#FACC15" strokeWidth="2">
                                <path d="M12 1L3 5v6c0 5.5 3.8 10.7 9 12c5.2-1.3 9-6.5 9-12V5l-9-4z"/>
                                <path d="M9 12l2 2l4-4"/>
                            </svg>
                        </div>
                        <h3 className="font-semibold text-lg text-gray-900">Secure Payment</h3>
                        <p className="text-sm text-gray-600 mt-2">
                            Your transactions are protected with bank-level security and encryption.
                        </p>
                    </div>

                </div>
            </section>


        </div>
    </div>
  );
};

export default EventList;

